import { IndexedDbConnection } from './indexed-db-connection';

/**
 * Clears all entries from all object stores in IndexedDB
 * @returns Promise that resolves when all stores have been cleared
 */
export async function purgeIndexedDB(): Promise<void> {
    try {
        const db = await IndexedDbConnection.getConnection();
        const storeNames = Array.from(db.objectStoreNames);

        if (storeNames.length === 0) {
            // Avoid circular dependency - don't use Logger here
            console.info('No object stores found in IndexedDB');
            return;
        }

        // Clear each object store
        const clearPromises = storeNames.map(async (storeName) => {
            try {
                const store = await IndexedDbConnection.getStoreConnection(storeName, 'readwrite');
                return new Promise<void>((resolve, reject) => {
                    const request = store.clear();
                    request.onsuccess = () => {
                        console.info(`Cleared IndexedDB store: ${storeName}`);
                        resolve();
                    };
                    request.onerror = () => {
                        console.error(`Failed to clear IndexedDB store: ${storeName}`, request.error);
                        reject(request.error);
                    };
                });
            } catch (error) {
                console.error(`Error accessing store ${storeName}:`, error);
                throw error;
            }
        });

        await Promise.all(clearPromises);
        console.info('Successfully cleared all IndexedDB stores');
    } catch (error) {
        console.error('Failed to purge IndexedDB:', error);
        throw error;
    }
}

/**
 * Clears entries from a specific object store in IndexedDB
 * @param storeName - Name of the object store to clear
 * @returns Promise that resolves when the store has been cleared
 */
export async function purgeIndexedDBStore(storeName: string): Promise<void> {
    try {
        const store = await IndexedDbConnection.getStoreConnection(storeName, 'readwrite');
        return new Promise<void>((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => {
                console.info(`Cleared IndexedDB store: ${storeName}`);
                resolve();
            };
            request.onerror = () => {
                console.error(`Failed to clear IndexedDB store: ${storeName}`, request.error);
                reject(request.error);
            };
        });
    } catch (error) {
        console.error(`Failed to purge IndexedDB store ${storeName}:`, error);
        throw error;
    }
}
